/**
 * The two browser steps of the image crop editor (ISSUE-1094): open the
 * picked file as a picture, and draw the framed, turned area into a new file.
 *
 * EXIF orientation: the browser applies it (CSS `image-orientation:
 * from-image`, the default) to `naturalWidth`/`naturalHeight`, to the
 * cropper's `<img>` and to `drawImage`. Verified on Chromium and WebKit in the
 * ISSUE-1094 plan. So a sideways phone photo opens upright, the frame and the
 * export agree, and this code never parses EXIF. The exported canvas carries
 * no metadata at all.
 *
 * One decoded element serves both display and export, so a phone decodes the
 * full-size picture once (no `createImageBitmap` second copy).
 */

import {
  buildCropDrawPlan,
  chooseOutputType,
  croppedFileName,
  IMAGE_CROP_OUTPUT_QUALITY,
  isCropOutputType,
  type typeCropArea,
  type typeCropRotation,
} from "./imageCropMath";

export type typeDecodedImage = {
  element: HTMLImageElement;
  objectUrl: string;
  /** Natural size after EXIF orientation. */
  width: number;
  height: number;
  /** The picked file's type and name. */
  sourceType: string;
  sourceName: string;
};

/**
 * Opens the picked file as a picture. The caller owns `objectUrl` and revokes
 * it when done. Rejects (and revokes) when the browser cannot open it.
 */
export async function decodeImageFile(file: File): Promise<typeDecodedImage> {
  const objectUrl = URL.createObjectURL(file);
  const element = new Image();
  element.src = objectUrl;
  try {
    await element.decode();
  } catch {
    URL.revokeObjectURL(objectUrl);
    throw new Error("#image-crop-01 the picked file could not be decoded");
  }
  if (element.naturalWidth < 1 || element.naturalHeight < 1) {
    URL.revokeObjectURL(objectUrl);
    throw new Error("#image-crop-01 the picked file could not be decoded");
  }
  return {
    element,
    objectUrl,
    width: element.naturalWidth,
    height: element.naturalHeight,
    sourceType: file.type,
    sourceName: file.name,
  };
}

/** Draws `area` of the turned picture into a new JPEG, WebP or PNG file. */
export async function renderCroppedFile(input: {
  image: CanvasImageSource;
  sourceWidth: number;
  sourceHeight: number;
  area: typeCropArea;
  rotation: typeCropRotation;
  maxEdgePx: number;
  sourceType: string;
  sourceName: string;
}): Promise<File> {
  const plan = buildCropDrawPlan({
    sourceWidth: input.sourceWidth,
    sourceHeight: input.sourceHeight,
    rotation: input.rotation,
    area: input.area,
    maxEdgePx: input.maxEdgePx,
  });
  const canvas = document.createElement("canvas");
  canvas.width = plan.outputWidth;
  canvas.height = plan.outputHeight;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("#image-crop-02 no 2d canvas context");
  }
  const preferredType = chooseOutputType(input.sourceType);
  context.imageSmoothingQuality = "high";
  if (preferredType === "image/jpeg") {
    // JPEG has no transparency: paint white so a transparent corner does not
    // turn black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, plan.outputWidth, plan.outputHeight);
  }
  context.scale(plan.scaleX, plan.scaleY);
  context.translate(plan.translateX, plan.translateY);
  context.rotate(plan.rotationRadians);
  context.drawImage(input.image, plan.drawX, plan.drawY);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, preferredType, IMAGE_CROP_OUTPUT_QUALITY);
  });
  // WebKit answers a WebP request with PNG; any of the three is accepted by
  // every upload hook and by media-api.
  if (blob === null || !isCropOutputType(blob.type)) {
    throw new Error("#image-crop-03 the canvas produced no usable image");
  }
  return new File([blob], croppedFileName(input.sourceName, blob.type), {
    type: blob.type,
  });
}
